/**
 * Help: what the exercise is, how to write a note by any of the three routes,
 * and the rule set being enforced.
 *
 * Written for someone who has just opened the app and has not read anything —
 * hence the ordering: what am I looking at, what do I do, what is it grading.
 */

import type { JSX } from 'react';
import { edoOf, rulesFor } from '../core';
import { useSession } from '../state/store';
import { Modal } from './Modal';

export function HelpDialog({ open, onClose }: { open: boolean; onClose: () => void }): JSX.Element {
  const settings = useSession((s) => s.settings);
  const exercise = useSession((s) => s.exercise);
  const rules = rulesFor(settings.profile);

  return (
    <Modal open={open} title="How this works" onClose={onClose} wide>
      <section className="help">
        <h3>The exercise</h3>
        <p>
          The plain line is the <b>cantus firmus</b> — a given melody you do not change. The violet line is yours.
          Write one note against each of its notes, all of them the same length, and the result is{' '}
          <b>first species counterpoint</b>. Every measure starts as a rest.
        </p>
        <p>
          This one uses <b>{edoOf(exercise)}-EDO</b> in <b>{exercise.key.label}</b>, {exercise.cf.length} measures,
          with the cantus firmus {exercise.cfPos === 'lower' ? 'below you' : 'above you'}.
        </p>

        <h3>Writing a note</h3>
        <ul>
          <li>
            <b>Touch</b> — press a measure, slide up or down to the note you want, and lift. While you press, every
            note the rules still allow there appears faintly.
          </li>
          <li>
            <b>Mouse</b> — hover a measure to see the same, and click to write. Click the note again to clear it back
            to a rest.
          </li>
          <li>
            <b>Keyboard</b> — Tab to the staff, then <kbd>←</kbd> <kbd>→</kbd> to choose the measure, <kbd>↑</kbd>{' '}
            <kbd>↓</kbd> to choose the pitch, <kbd>Enter</kbd> to write it and <kbd>Delete</kbd> to clear it. In
            31-EDO, the vertical arrows visit all 31 circle-of-fifths spellings, including double flats and sharps.
          </li>
        </ul>

        <h3>Shortcuts</h3>
        <dl className="shortcuts">
          <div>
            <dt>
              <kbd>Space</kbd>
            </dt>
            <dd>Play or pause</dd>
          </div>
          <div>
            <dt>
              <kbd>⌘Z</kbd> / <kbd>⇧⌘Z</kbd>
            </dt>
            <dd>Undo, redo</dd>
          </div>
          <div>
            <dt>
              <kbd>G</kbd>
            </dt>
            <dd>Grade</dd>
          </div>
          <div>
            <dt>
              <kbd>N</kbd>
            </dt>
            <dd>New exercise</dd>
          </div>
          <div>
            <dt>
              <kbd>T</kbd>
            </dt>
            <dd>Tap tempo</dd>
          </div>
          <div>
            <dt>
              <kbd>?</kbd>
            </dt>
            <dd>This page</dd>
          </div>
        </dl>

        <h3>What the grader wants ({rules.label})</h3>
        <ol>
          <li>
            Only consonant verticals: <b>3rds, 5ths, 6ths and octaves</b>. The perfect fourth counts as a dissonance
            between two voices.
          </li>
          <li>
            Open and close on the tonic. {rules.blurb}
          </li>
          <li>No parallel fifths or octaves, and no rushing into one in similar motion.</li>
          <li>At most three 3rds or 6ths in a row — beyond that the voices stop sounding independent.</li>
          <li>Mostly contrary motion, mostly stepwise, aimed at a single high point.</li>
        </ol>

        <h3>Assists, and what they cost</h3>
        <p>
          The two assists — interval labels and allowed notes — state <em>facts</em>, never verdicts. Nothing tells you
          a note is wrong while you write. That is deliberate: the wrongness is meant to be found by your own ear, or
          at grading time.
        </p>
        <p>
          If an assist was on while you were writing, the dimensions it helped with are marked <b>assisted</b> in the
          report. A cold score means writing the line with both switched off.
        </p>
      </section>
    </Modal>
  );
}
